import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../features/auth/hooks/useAuthStore'
import { navigationConfig, type NavigationItem } from '../config/navigationConfig'
import { organizationAdminNavigationConfig } from '../config/organizationAdminNavigationConfig'
import { ChevronDown, School2 } from 'lucide-react'
import { useOrganizationProfile } from '../shared/hooks/useOrganizationProfile'
import { getStoredRoleHomePath, normalizeStoredRole } from '../routes/auth'
import { isBranchAdminRole as checkIsBranchAdminRole } from '../shared/utils/roleUtils'

export type FeesFinanceNavKey =
  | 'overview'
  | 'fee-head'
  | 'fee-structure'
  | 'reports'
  | 'payments'
  | 'discounts'
  | 'approvals'
  | null

export function getFeesFinanceActiveKey(location: { pathname: string; search?: string }): FeesFinanceNavKey {
  const current = location.pathname || ''
  const search = location.search || ''

  const isFeesFinance =
    current.startsWith('/admin/finance/fees-finance') ||
    current.startsWith('/admin/fees-finance') ||
    current.startsWith('/admin/finance/fee-head') ||
    current.startsWith('/admin/fees-finance/fee-head') ||
    current.startsWith('/admin/finance/fee-structure') ||
    current.startsWith('/admin/fees-finance/fee-structure')

  if (!isFeesFinance) return null

  // 1. Explicit path sub-routes (takes precedence over query parameter)
  if (
    current.startsWith('/admin/finance/fee-head') ||
    current.startsWith('/admin/fees-finance/fee-head') ||
    current === '/admin/finance/fees-finance/fee-head' ||
    current.startsWith('/admin/finance/fees-finance/fee-head/') ||
    current === '/admin/fees-finance/fee-head' ||
    current.startsWith('/admin/fees-finance/fee-head/')
  ) {
    return 'fee-head'
  }

  if (
    current.startsWith('/admin/finance/fee-structure') ||
    current.startsWith('/admin/fees-finance/fee-structure') ||
    current === '/admin/finance/fees-finance/fee-structure' ||
    current.startsWith('/admin/finance/fees-finance/fee-structure/') ||
    current === '/admin/fees-finance/fee-structure' ||
    current.startsWith('/admin/fees-finance/fee-structure/')
  ) {
    return 'fee-structure'
  }

  if (
    current === '/admin/finance/fees-finance/reports' ||
    current.startsWith('/admin/finance/fees-finance/reports/') ||
    current === '/admin/fees-finance/reports' ||
    current.startsWith('/admin/fees-finance/reports/')
  ) {
    return 'reports'
  }

  if (
    current === '/admin/finance/fees-finance/payments' ||
    current.startsWith('/admin/finance/fees-finance/payments/') ||
    current === '/admin/fees-finance/payments' ||
    current.startsWith('/admin/fees-finance/payments/')
  ) {
    return 'payments'
  }

  if (
    current === '/admin/finance/fees-finance/discounts' ||
    current.startsWith('/admin/finance/fees-finance/discounts/') ||
    current === '/admin/fees-finance/discounts' ||
    current.startsWith('/admin/fees-finance/discounts/')
  ) {
    return 'discounts'
  }

  if (
    current === '/admin/finance/fees-finance/approvals' ||
    current.startsWith('/admin/finance/fees-finance/approvals/') ||
    current === '/admin/fees-finance/approvals' ||
    current.startsWith('/admin/fees-finance/approvals/')
  ) {
    return 'approvals'
  }

  if (
    current === '/admin/finance/fees-finance/overview' ||
    current.startsWith('/admin/finance/fees-finance/overview/') ||
    current === '/admin/fees-finance/overview' ||
    current.startsWith('/admin/fees-finance/overview/')
  ) {
    return 'overview'
  }

  // 2. Base path with query parameter or default overview tab
  const isBase =
    current === '/admin/finance/fees-finance' ||
    current === '/admin/finance/fees-finance/' ||
    current === '/admin/fees-finance' ||
    current === '/admin/fees-finance/'

  if (isBase) {
    const params = new URLSearchParams(search)
    const tab = params.get('tab')
    if (tab === 'fee-head') return 'fee-head'
    if (tab === 'fee-structure') return 'fee-structure'
    if (tab === 'reports') return 'reports'
    if (tab === 'payments') return 'payments'
    if (tab === 'discounts') return 'discounts'
    if (tab === 'approvals') return 'approvals'
    if (tab === 'overview' || !tab) return 'overview'
  }

  return null
}

interface PermissionAwareSidebarProps {
  open: boolean
  onClose: () => void
}

export function PermissionAwareSidebar({ open, onClose }: PermissionAwareSidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission, role, user } = useAuthStore()
  const [openParents, setOpenParents] = useState<Record<string, boolean>>({})

  const { data: orgProfile } = useOrganizationProfile()
  const [imgError, setImgError] = useState(false)

  const schoolName =
    orgProfile?.organization?.orgName ||
    orgProfile?.branch?.branchName ||
    orgProfile?.name ||
    orgProfile?.schoolName ||
    orgProfile?.organizationName ||
    orgProfile?.orgName ||
    orgProfile?.title

  const logoUrl =
    orgProfile?.logoUrl ||
    orgProfile?.logo ||
    orgProfile?.logo_url ||
    orgProfile?.organization?.logoUrl ||
    orgProfile?.branch?.logoUrl ||
    orgProfile?.shortLivedLogoUrl ||
    null

  useEffect(() => {
    setImgError(false)
  }, [logoUrl])

  const normalizedRole = normalizeStoredRole(role)

  const isOrgAdmin =
    normalizedRole === 'organizational_admin' ||
    normalizedRole === 'org_admin' ||
    normalizedRole === 'organization_admin' ||
    role === 'organizational_admin' ||
    role === 'org_admin' ||
    role === 'organization_admin' ||
    role === 'ORGANIZATIONAL_ADMIN' ||
    role === 'ORG_ADMIN' ||
    location.pathname.startsWith('/organization-admin') ||
    location.pathname.startsWith('/admin/organization-dashboard')

  // Resolve target path dynamically for general paths like '/dashboard' or '/communication'
  const resolveItemPath = (item: NavigationItem): string => {
    if (item.path === '/dashboard') {
      if (isOrgAdmin) {
        return '/organization-admin/dashboard'
      }
      if (normalizedRole === 'teacher') {
        return '/teacher/dashboard'
      }
      if (normalizedRole === 'admin' || hasPermission('*')) {
        return '/admin/dashboard'
      }
      return getStoredRoleHomePath()
    }
    if (item.path === '/communication') {
      if (normalizedRole === 'teacher') {
        return '/teacher/communication'
      }
      if (normalizedRole === 'admin' || hasPermission('*')) {
        return '/admin/communication'
      }
    }
    if (item.path === '/teacher/lessonplans' || item.path === '/admin/lessonplans' || item.label === 'Lesson Plans') {
      if (normalizedRole === 'admin' || role === 'admin' || hasPermission('*')) {
        return '/admin/lessonplans'
      }
      return '/teacher/lessonplans'
    }
    return item.path
  }

  // Filter sections and items strictly based on user permissions
  const targetConfig = isOrgAdmin ? organizationAdminNavigationConfig : navigationConfig

  const filteredSections = targetConfig
    .map((section) => {
      const allowedItems = section.items.filter((item) => {
        const isTeacher =
          role === 'teacher' ||
          role === 'TEACHER' ||
          role === 'staff' ||
          role === 'STAFF' ||
          normalizedRole === 'teacher'

        const isAdminOrBranchAdmin =
          checkIsBranchAdminRole(role, user) ||
          role === 'admin' ||
          role === 'superadmin' ||
          role === 'branch_admin' ||
          role === 'branchadmin' ||
          role === 'organizational_admin' ||
          role === 'org_admin' ||
          role === 'organization_admin' ||
          normalizedRole === 'admin' ||
          hasPermission('*')

        // Teacher Exam Management: ONLY visible to teachers/staff, NEVER to admin
        if (item.path === '/teacher/exam-management' || item.label === 'Exam Management') {
          if (isAdminOrBranchAdmin && !isTeacher) {
            return false
          }
          return isTeacher || hasPermission('assignments:view') || hasPermission('attendance:view')
        }

        // Admin Examination Suite: ONLY visible to admin, NEVER to teachers
        if (
          item.path === '/admin/academic/examinations' ||
          item.path.startsWith('/admin/academic/examinations') ||
          (item.label === 'Examination' && item.children && item.children.length > 0)
        ) {
          if (isTeacher && !isAdminOrBranchAdmin) {
            return false
          }
          return isAdminOrBranchAdmin || hasPermission('academics:view')
        }

        // Staff Attendance: ONLY visible to staff/teacher roles, NEVER to branch admin or other admin roles
        if (item.path === '/teacher/attendance' || item.label === 'Staff Attendance') {
          if (isAdminOrBranchAdmin) {
            return false
          }
          return isTeacher
        }

        if (isAdminOrBranchAdmin) {
          if (item.path.startsWith('/teacher-library')) {
            return false
          }
          return true
        }

       
        if (item.path.startsWith('/teacher-library')) {
          return (
            hasPermission('library:view') ||
            hasPermission('library.view') ||
            role === 'teacher' ||
            role === 'TEACHER' ||
            role === 'staff' ||
            role === 'STAFF'
          )
        }

         if (item.path === '/admin/enrollment/class-section-setup' || item.path === '/admin/enrollment/seat-management' || item.path === '/admin/class-management/classes') {
          return isAdminOrBranchAdmin
        }

        if (item.path === '/admin/academics' || item.permission === 'academics:view' || item.permission === 'academics.view') {
          return hasPermission('academics.view') || hasPermission('academics:view')
        }

        if (item.path === '/teacher/myclasses' || item.label === 'My Classes') {
          return (
            hasPermission('class-management:view') ||
            hasPermission('class-management.view') ||
            hasPermission('sections:view') ||
            hasPermission('sections.view') ||
            hasPermission('grades:view') ||
            hasPermission('grades.view') ||
            hasPermission('students:view') ||
            hasPermission('students.view') ||
            isTeacher
          )
        }

        return hasPermission(item.permission)
      })
      return {
        ...section,
        items: allowedItems,
      }
    })
    .filter((section) => section.items.length > 0)

  const toggleParent = (label: string) => {
    setOpenParents((prev) => ({ ...prev, [label]: !prev[label] }))
  }

  const handleNavClick = (item: NavigationItem) => {
    if (item.children && item.children.length > 0) {
      toggleParent(item.label)
      return
    }
    const targetPath = resolveItemPath(item)
    if (targetPath) {
      navigate(targetPath)
      onClose()
      window.setTimeout(() => {
        if (window.location.pathname === targetPath && document.querySelector('h1')?.textContent?.includes('Revision Mode')) {
          window.location.href = targetPath
        }
      }, 50)
    }
  }

  const isPathActive = (targetPath: string): boolean => {
    if (!targetPath) return false
    const current = location.pathname

    if (current === targetPath) return true

    // 0. Academics overview rule: do not activate Academics when on Subject Management
    if (targetPath === '/admin/academics') {
      if (current.startsWith('/admin/academics/subject-management') || current.startsWith('/admin/academics/subject-allocation')) {
        return false
      }
      if (current.startsWith('/admin/academics/overview')) {
        return true
      }
      return false
    }

    if (targetPath === '/admin/academics/subject-management') {
      if (
        current.startsWith('/admin/academics/subject-allocation') ||
        current.startsWith('/admin/class-management/subject-allocation') ||
        location.search.includes('view=allocate')
      ) {
        return false
      }
    }

    if (
      targetPath === '/admin/academics/subject-allocation' ||
      targetPath === '/admin/class-management/subject-allocation'
    ) {
      if (
        current.startsWith('/admin/academics/subject-allocation') ||
        current.startsWith('/admin/class-management/subject-allocation') ||
        location.search.includes('view=allocate')
      ) {
        return true
      }
    }

    if (targetPath === '/organization-admin/user-management') {
      if (
        current === '/organization-admin/user-management' ||
        current === '/organization-admin/user-management/' ||
        current.startsWith('/organization-admin/user-management')
      ) {
        return true
      }
    }

    if (targetPath === '/organization-admin/role-management') {
      if (
        current === '/organization-admin/role-management' ||
        current.startsWith('/organization-admin/role-management')
      ) {
        return true
      }
    }

    // 9. User & Role Management Sub-routes (must be evaluated BEFORE generic startsWith)
    if (targetPath === '/admin/user-management') {
      if (
        current === '/admin/user-management' ||
        current === '/admin/user-management/' ||
        current.startsWith('/admin/user-management/users') ||
        current.startsWith('/admin/user-management/create-user') ||
        current.startsWith('/admin/user-management/overview') ||
        current.startsWith('/admin/user-management/user-details')
      ) {
        return true
      }
      return false
    }

    if (targetPath === '/admin/user-management/role-management') {
      if (
        current === '/admin/user-management/role-management' ||
        current.startsWith('/admin/user-management/role-management') ||
        current.startsWith('/admin/user-management/create-role')
      ) {
        return true
      }
    }

    if (targetPath === '/admin/user-management/departments') {
      if (current.startsWith('/admin/user-management/departments')) {
        return true
      }
    }

    if (targetPath === '/admin/user-management/designations') {
      if (current.startsWith('/admin/user-management/designations')) {
        return true
      }
    }

    if (targetPath === '/admin/audit-logs') {
      if (current === '/admin/audit-logs' || current.startsWith('/admin/audit-logs/')) {
        return true
      }
    }

    // Communication specific routes must be evaluated before generic startsWith!
    if (targetPath === '/communication') {
      if (
        current.startsWith('/communication/groups') ||
        current.startsWith('/communication/message') ||
        current.startsWith('/communication/messages') ||
        current.startsWith('/communication/notices') ||
        current.startsWith('/communication/compose')
      ) {
        return false
      }
      return (
        current === '/communication' ||
        current === '/communication/' ||
        current === '/communication/dashboard'
      )
    }

    if (targetPath === '/communication/groups') {
      return (
        current === '/communication/groups' ||
        current.startsWith('/communication/groups/')
      )
    }

    if (targetPath === '/communication/message') {
      if (
        current.startsWith('/communication/compose') ||
        current.startsWith('/teacher/communication/compose') ||
        current.startsWith('/communication/message') ||
        current.startsWith('/communication/messages')
      ) {
        return true
      }
      return false
    }

    if (targetPath === '/communication/notices') {
      if (
        current.startsWith('/communication/notices') ||
        current.startsWith('/communication/notice')
      ) {
        return true
      }
      return false
    }

    if (current.startsWith(targetPath + '/')) return true

    // 2. Student Directory Sub-routes
    if (targetPath === '/admin/student-directory') {
      if (
        current.startsWith('/admin/student-directory') ||
        current.startsWith('/admin/add-student') ||
        current.startsWith('/admin/student-profile') ||
        current.startsWith('/student/')
      ) {
        return true
      }
      return false
    }

    // 3. Teacher/Staff Directory Sub-routes
    if (targetPath === '/admin/frontoffice/staff-directory') {
      if (
        current.startsWith('/admin/frontoffice/staff') ||
        current.startsWith('/admin/staff-profile') ||
        current.startsWith('/admin/staff-directory')
      ) {
        return true
      }
    }

    // 4. Admission Application & Process Sub-routes
    if (targetPath === '/admin/admission-application') {
      if (
        current.startsWith('/admin/admission-application') ||
        current.startsWith('/admin/frontoffice/admission-application')
      ) {
        return true
      }
    }

    if (targetPath === '/admin/admission-process') {
      if (
        current.startsWith('/admin/admission-process') ||
        current.startsWith('/admin/frontoffice/admission-process')
      ) {
        return true
      }
    }

    // 5. Seat Management & Admissions Sub-routes
    if (targetPath === '/admin/enrollment/class-section-setup' || targetPath === '/admin/enrollment/seat-management') {
      if (
        current.startsWith('/admin/enrollment') ||
        current.startsWith('/admin/admissions')
      ) {
        return true
      }
    }

    // 6. Timetable Sub-routes
    if (targetPath === '/academics/timetable') {
      if (
        current.startsWith('/academics/timetable') ||
        current.startsWith('/admin/academics/timetable') ||
        current.startsWith('/teacher/academics/timetable')
      ) {
        return true
      }
    }

    if (targetPath === '/admin/events-calendar') {
      if (
        current.startsWith('/admin/events-calendar') ||
        current.startsWith('/admin/calendar') ||
        current.startsWith('/admin/events/calendar')
      ) {
        return true
      }
    }

    // 7. Student Lifecycle Sub-routes
    if (targetPath === '/admin/student-lifecycle') {
      if (
        current.startsWith('/admin/student-lifecycle') ||
        current.startsWith('/admin/class-management/transfer-students') ||
        current.startsWith('/admin/class-management/promote-students')
      ) {
        return true
      }
    }

    // 8. Class Management Sub-routes
    if (targetPath === '/admin/class-management/classes') {
      if (
        current.startsWith('/admin/class-management/classes') ||
        current.startsWith('/admin/class-management/assign-students')
      ) {
        return true
      }
    }

    // 8. Lesson Plans Sub-routes
    if (targetPath === '/teacher/lessonplans' || targetPath === '/admin/lessonplans') {
      if (
        current.startsWith('/teacher/lessonplans') ||
        current.startsWith('/admin/lessonplans') ||
        current.startsWith('/teacher/create-lesson-plan')
      ) {
        return true
      }
    }

    // 10. Fee Structure Sub-routes
    if (targetPath === '/admin/finance/fees-finance/fee-structure' || targetPath === '/admin/fees-finance/fee-structure') {
      if (
        current.startsWith('/admin/finance/fee-structure') ||
        current.startsWith('/admin/fees-finance/fee-structure') ||
        current.startsWith('/admin/finance/fees-finance/fee-structure')
      ) {
        return true
      }
    }

    return false
  }

  const isChildActive = (parentItem: NavigationItem, childItem: NavigationItem): boolean => {
    const isFeesFinanceParent = parentItem.label === 'Fees & Finance' || parentItem.path.includes('fees-finance')
    if (isFeesFinanceParent) {
      const activeKey = getFeesFinanceActiveKey(location)
      if (!activeKey) return false
      const p = childItem.path.toLowerCase()
      if (p.endsWith('/overview') || p.includes('tab=overview')) return activeKey === 'overview'
      if (p.endsWith('/fee-head') || p.includes('tab=fee-head')) return activeKey === 'fee-head'
      if (p.endsWith('/fee-structure') || p.includes('tab=fee-structure')) return activeKey === 'fee-structure'
      if (p.endsWith('/payments') || p.includes('tab=payments')) return activeKey === 'payments'
      if (p.endsWith('/discounts') || p.includes('tab=discounts')) return activeKey === 'discounts'
      if (p.endsWith('/reports') || p.includes('tab=reports')) return activeKey === 'reports'
      if (p.endsWith('/approvals') || p.includes('tab=approvals')) return activeKey === 'approvals'
      return false
    }
    return isPathActive(childItem.path)
  }

  const isParentActive = (item: NavigationItem): boolean => {
    if (item.children && item.children.length > 0) {
      return item.children.some((child) => isChildActive(item, child))
    }
    const targetPath = resolveItemPath(item)
    if (item.exact) {
      return location.pathname === targetPath
    }
    return isPathActive(targetPath)
  }

  const isParentExpanded = (item: NavigationItem): boolean => {
    if (!item.children || item.children.length === 0) return false
    if (openParents[item.label] !== undefined) {
      return openParents[item.label]
    }
    return item.children.some((child) => isChildActive(item, child))
  }

  const normalizedRoleStr = (role || user?.role || user?.userType || (user as any)?.assignedRole?.roleName || '').toString().toLowerCase()
  const isBranchAdminRole =
    normalizedRoleStr.includes('branch_admin') ||
    normalizedRoleStr.includes('branchadmin') ||
    normalizedRoleStr.includes('branch admin')

  const portalSublabel = isOrgAdmin
    ? 'Organization Administrator'
    : isBranchAdminRole
    ? 'Branch Admin'
    : role === 'admin'
    ? 'School Administrator'
    : role === 'teacher'
    ? 'Teacher Portal'
    : `${user?.name || 'User'} Portal`

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 flex w-[240px] flex-col overflow-y-auto bg-[#005156] px-4 pb-5 pt-4 text-white text-[14px] font-['Inter',sans-serif] transition-transform duration-300 lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .sidebar-scroll-custom {
              scrollbar-width: none;
              -ms-overflow-style: none;
            }
            .sidebar-scroll-custom::-webkit-scrollbar {
              width: 0;
              height: 0;
            }
          `,
        }}
      />

      {/* Brand & Portal Header */}
      <div
        className="flex cursor-pointer items-center gap-3 rounded-3xl px-3 py-2"
        onClick={() => {
          navigate(
            isOrgAdmin
              ? '/organization-admin/dashboard'
              : role === 'teacher'
                ? '/teacher/dashboard'
                : '/admin/dashboard'
          )
          onClose()
        }}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/18 bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] overflow-hidden">
          {logoUrl && !imgError ? (
            <img
              src={logoUrl}
              alt={schoolName}
              onError={() => setImgError(true)}
              className="h-full w-full object-contain p-1"
            />
          ) : (
            <School2 className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-[15px] font-bold leading-none text-white tracking-tight truncate" title={schoolName || 'School App'}>
            {schoolName || 'School App'}
          </h1>
          <p className="mt-1.5 text-[11px] font-medium text-white/65 leading-none">
            {portalSublabel}
          </p>
        </div>
      </div>

      {/* Navigation Sections */}
      <nav className="sidebar-scroll-custom mt-6 flex flex-1 flex-col pr-1">
        {filteredSections.map((section, idx) => (
          <div key={section.title || `section-${idx}`} className="mb-4">
            {section.title ? (
              <p className="px-3 text-[11px] font-bold uppercase tracking-[1.1px] text-white/40 mt-3 mb-2">
                {section.title}
              </p>
            ) : null}

            <div className="flex flex-col gap-1">
              {section.items.map((item) => {
                const Icon = item.icon
                const active = isParentActive(item)
                const hasChildren = Boolean(item.children && item.children.length > 0)
                const isExpanded = isParentExpanded(item)

                return (
                  <div key={item.label}>
                    <button
                      type="button"
                      aria-current={active ? 'page' : undefined}
                      onClick={() => handleNavClick(item)}
                      className={`flex w-full items-center gap-3 rounded-r-lg rounded-l-none pl-3 pr-3 py-2.5 text-left text-[14px] font-semibold transition border-l-4 ${
                        active
                          ? 'bg-white/15 text-white border-l-[#b4ecf2] border-y-0 border-r-0'
                          : 'border-l-transparent border-y-0 border-r-0 text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-white' : 'text-white/70'}`} />
                      <span className="flex-1 whitespace-normal leading-snug break-words">{item.label}</span>
                      {hasChildren && (
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </button>

                    {/* Child Menu Items */}
                    {hasChildren && isExpanded && (
                      <div className="space-y-1 pl-3 mt-1">
                        {item.children?.map((child) => {
                          const ChildIcon = child.icon || Icon
                          const childActive = isChildActive(item, child)

                          return (
                            <button
                              key={child.label}
                              type="button"
                              aria-current={childActive ? 'page' : undefined}
                              onClick={() => {
                                const targetPath = child.path
                                navigate(targetPath)
                                onClose()
                                window.setTimeout(() => {
                                  if (window.location.pathname === targetPath && document.querySelector('h1')?.textContent?.includes('Revision Mode')) {
                                    window.location.href = targetPath
                                  }
                                }, 50)
                              }}
                              className={`flex w-full items-center gap-2.5 rounded-r-lg rounded-l-none pl-6 pr-3 py-2 text-left text-[13px] font-medium transition ${
                                childActive
                                  ? 'bg-white/10 text-white font-semibold'
                                  : 'text-white/70 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              <ChildIcon className={`h-[16px] w-[16px] shrink-0 ${childActive ? 'text-white' : 'text-white/70'}`} />
                              <span className="flex-1 whitespace-normal leading-snug break-words">{child.label}</span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  )
}

export default PermissionAwareSidebar
